import { computed, ref } from 'vue';
import camelcaseKeys from 'camelcase-keys';
import AudiencesAPI from 'dashboard/api/audiences';

const DEBOUNCE_MS = 400;
const emptyResult = () => ({
  count: 0,
  withoutPhoneCount: 0,
  contacts: [],
});

// Live preview of the audience being edited: waits for the user to stop
// changing conditions, cancels the request it supersedes and only ever shows
// the answer to the latest one. A failed request never leaves the previous
// count on screen: `error` is 'incomplete' (422: a condition is still being
// filled in) or 'failed' (network or server error).
export function useAudiencePreview() {
  const result = ref(emptyResult());
  const error = ref(null);
  const isWaiting = ref(false);
  const isRequesting = ref(false);

  let timer = null;
  let controller = null;
  let latestRequestId = 0;

  const request = async criteria => {
    controller?.abort();
    controller = new AbortController();
    latestRequestId += 1;
    const requestId = latestRequestId;
    isWaiting.value = false;
    isRequesting.value = true;
    try {
      const { data } = await AudiencesAPI.preview(criteria, controller.signal);
      if (requestId !== latestRequestId) return;
      result.value = {
        count: data.meta?.count || 0,
        withoutPhoneCount: data.meta?.without_phone_count || 0,
        contacts: camelcaseKeys(data.payload || [], {
          deep: true,
          stopPaths: ['custom_attributes', 'additional_attributes'],
        }),
      };
      error.value = null;
    } catch (requestError) {
      if (requestId !== latestRequestId) return;
      result.value = emptyResult();
      error.value =
        requestError?.response?.status === 422 ? 'incomplete' : 'failed';
    } finally {
      if (requestId === latestRequestId) isRequesting.value = false;
    }
  };

  const schedule = getCriteria => {
    isWaiting.value = true;
    clearTimeout(timer);
    timer = setTimeout(() => request(getCriteria()), DEBOUNCE_MS);
  };

  // Drops whatever is pending or in flight and forgets the last answer.
  const reset = () => {
    clearTimeout(timer);
    controller?.abort();
    latestRequestId += 1;
    isWaiting.value = false;
    isRequesting.value = false;
    result.value = emptyResult();
    error.value = null;
  };

  return {
    result,
    error,
    isLoading: computed(() => isWaiting.value || isRequesting.value),
    schedule,
    reset,
  };
}
